import React, { type FC, type SVGProps } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import Dialog from '../Dialog/Dialog';
import Button from '../Button/Button';
import Icon from '../Icon/Icon';

import styles from './ConfirmationDialog.module.scss';

type Props = {
  open: boolean;
  title: string;
  body: string;
  onConfirm: () => void;
  onClose: () => void;
  confirmLabel?: string;
  closeLabel?: string;
  variant?: 'default' | 'delete';
  busy?: boolean;
  icon?: FC<SVGProps<SVGSVGElement>>;
};

const ConfirmationDialog: React.FC<Props> = ({
  open,
  title,
  body,
  onConfirm,
  onClose,
  confirmLabel,
  closeLabel,
  variant = 'default',
  busy,
  icon,
}: Props) => {
  const { t } = useTranslation('common');

  return (
    <Dialog open={open} onClose={onClose} role="alertdialog" size="small-2">
      {icon ? (
        <div className={styles.iconWrap} aria-hidden="true">
          <Icon icon={icon} className={styles.icon} />
        </div>
      ) : null}
      <h2 className={styles.title}>{title}</h2>
      <p className={styles.body}>{body}</p>
      <div className={classNames(styles.buttons, { [styles.deleteButtons]: variant === 'delete' })}>
        <Button
          className={styles.confirmButton}
          label={confirmLabel || t('confirmation_dialog.confirm')}
          variant={variant === 'delete' ? 'transparent' : 'contained'}
          color={variant === 'delete' ? 'default' : busy ? 'default' : 'primary'}
          onClick={onConfirm}
          fullWidth={variant !== 'delete'}
          disabled={busy}
          busy={busy}
        />
        <Button
          className={styles.closeButton}
          label={closeLabel || t('confirmation_dialog.close')}
          variant={variant === 'delete' ? 'contained' : 'transparent'}
          color={variant === 'delete' ? 'primary' : 'default'}
          onClick={onClose}
          fullWidth={variant !== 'delete'}
          disabled={busy}
        />
      </div>
    </Dialog>
  );
};

export default ConfirmationDialog;
